import GlobalErrorBoundary from '@/components/GlobalErrorBoundary';
import { AsyncErrorBoundary } from '@/components/AsyncErrorBoundary';
import { useAuth } from '@/components/AuthContext';
import { useUserRole } from '@/hooks';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { AdminLayout } from '@/components/admin';
import RevisionBadge from '@/components/RevisionBadge';
import { StatusGrid } from '@/features/admin/components/StatusGrid';
import { SubscriberStatsWidget } from '@/features/admin/components/widgets/SubscriberStatsWidget';
import { SystemResourcesWidget } from '@/features/admin/components/widgets/SystemResourcesWidget';
import { AdvertisingStatsWidget } from '@/features/admin/components/widgets/AdvertisingStatsWidget';
import { SalesStatsWidget } from '@/features/admin/components/widgets/SalesStatsWidget';
import { SecurityStatsWidget } from '@/features/admin/components/widgets/SecurityStatsWidget';
import { SystemMonitoringChart } from '@/features/admin/components/widgets/SystemMonitoringChart';
import { DatabaseHealthWidget } from '@/features/admin/components/widgets/DatabaseHealthWidget';
import { SessionStatsWidget } from '@/features/admin/components/widgets/SessionStatsWidget';
import { BackupStatusWidget } from '@/features/admin/components/widgets/BackupStatusWidget';
import { LiveMetricsDashboard } from '@/components/admin/LiveMetricsDashboard';
import { RealtimeSystemMonitor } from '@/components/admin/RealtimeSystemMonitor';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Activity, BarChart3 } from 'lucide-react';
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { productionLogger } from '@/utils';

export default function AdminDashboard() {
  const { user, loading: authLoading } = useAuth();
  const { isAdmin, loading: roleLoading } = useUserRole();
  const [activeTab, setActiveTab] = useState('overview');
  const [systemInfo, setSystemInfo] = useState({
    version: 'v2.0.0', // Static version since package.json import causes issues
    lastBackup: 'Loading...'
  });
  const [recentActivity, setRecentActivity] = useState<Array<{
    event: string;
    timestamp: string;
  }>>([]);

  // Helper functions - match BackupStatusWidget formatting
  const formatRelativeTime = (date: Date): string => {
    const now = new Date();
    const diffMs = date.getTime() - now.getTime();
    const diffHours = Math.abs(diffMs) / (1000 * 60 * 60);
    const diffDays = Math.abs(diffMs) / (1000 * 60 * 60 * 24);

    if (diffMs < 0) {
      // Past time
      if (diffHours < 1) {
        const minutes = Math.floor(diffHours * 60);
        return `${minutes} minutes ago`;
      } else if (diffHours < 48) {
        return `${Math.floor(diffHours)} hours ago`;
      } else {
        return `${Math.floor(diffDays)} days ago`;
      }
    } else {
      // Future time
      if (diffHours < 1) {
        const minutes = Math.floor(diffHours * 60);
        return `in ${minutes} minutes`;
      } else if (diffHours < 48) {
        return `in ${Math.floor(diffHours)} hours`;
      } else {
        return `in ${Math.floor(diffDays)} days`;
      }
    }
  };

  const formatEventType = (eventType: string): string => {
    return eventType
      .replace(/_/g, ' ')
      .replace(/\b\w/g, l => l.toUpperCase());
  };

  // Fetch real system info and recent activity - MUST be before early returns
  useEffect(() => {
    const fetchSystemInfo = async () => {
      try {
        // Get last backup timestamp
        const backupResult = await supabase.rpc('get_latest_backup_timestamp');
        
        let lastBackupString = 'No backup found';
        if (backupResult.data && !backupResult.error) {
          const backupDate = new Date(backupResult.data);
          lastBackupString = formatRelativeTime(backupDate);
        } else if (backupResult.error) {
          productionLogger.error('Error fetching backup timestamp:', backupResult.error);
          lastBackupString = 'Error loading backup data';
        }

        // Calculate next backup time (daily at 3 AM ET)
        let nextBackupString = 'Not scheduled';
        try {
          const nextBackupResult = await supabase.rpc('calculate_next_backup_run', {
            p_frequency: 'daily',
            p_from_time: new Date().toISOString()
          });
          
          if (nextBackupResult.data && !nextBackupResult.error) {
            const nextBackupDate = new Date(nextBackupResult.data);
            nextBackupString = formatRelativeTime(nextBackupDate);
          }
        } catch (error) {
          productionLogger.error('Error calculating next backup:', error);
          nextBackupString = 'Calculation error';
        }

        setSystemInfo({
          version: 'v2.0.0', // Static version
          lastBackup: `${lastBackupString} | Next: ${nextBackupString}`
        });
      } catch (error) {
        productionLogger.error('Error fetching system info:', error);
        setSystemInfo({
          version: 'v2.0.0',
          lastBackup: 'Error loading backup data'
        });
      }
    };

    // Fetch recent activity
    const fetchRecentActivity = async () => {
      try {
        const { data: events } = await supabase
          .from('security_events')
          .select('event_type, created_at, details')
          .order('created_at', { ascending: false })
          .limit(5);

        if (events) {
          const activities = events.map(event => ({
            event: formatEventType(event.event_type),
            timestamp: formatRelativeTime(new Date(event.created_at))
          }));
          setRecentActivity(activities);
        }
      } catch (error) {
        productionLogger.error('Error fetching recent activity:', error);
        setRecentActivity([
          { event: 'Unable to load recent activity', timestamp: 'Error' }
        ]);
      }
    };

    fetchSystemInfo();
    fetchRecentActivity();
    
    // Refresh every 30 seconds
    const interval = setInterval(() => {
      fetchSystemInfo();
      fetchRecentActivity();
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  // Show loading state while checking auth - AFTER hooks
  if (authLoading || roleLoading) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }

  return (
    <AdminLayout showDashboardHeader={true}>
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-3xl font-bold">Admin Dashboard</h1>
          <TabsList className="grid w-auto grid-cols-4 gap-1">
            <TabsTrigger value="overview" className="flex items-center gap-2">
              <BarChart3 className="h-4 w-4" />
              Overview
            </TabsTrigger>
            <TabsTrigger value="live-metrics" className="flex items-center gap-2">
              <Activity className="h-4 w-4" />
              Live Metrics
            </TabsTrigger>
            <TabsTrigger value="system-monitor" className="flex items-center gap-2">
              <Activity className="h-4 w-4" />
              System Monitor
            </TabsTrigger>
            <TabsTrigger value="backup" className="flex items-center gap-2">
              Backup Status
            </TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="overview" className="space-y-4">
          {/* Row 1: System Status + Moved Widgets */}
          <div className="grid gap-4 md:grid-cols-4 mb-4">
            <div className="md:col-span-1">
              <StatusGrid />
            </div>
            <div className="md:col-span-1">
              <SystemResourcesWidget />
            </div>
            <div className="md:col-span-1">
              <DatabaseHealthWidget />
            </div>
            <div className="md:col-span-1">
              <SecurityStatsWidget />
            </div>
          </div>

          {/* Row 2: Keep unchanged - Key Stats */}
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4 mb-4">
            <SubscriberStatsWidget />
            <SalesStatsWidget />
            <SessionStatsWidget />
            <AdvertisingStatsWidget />
          </div>

          {/* Row 3: System Monitoring Chart (3/4) + Backup Status (1/4) */}
          <div className="grid gap-4 md:grid-cols-4 mb-4">
            <div className="md:col-span-3">
              <SystemMonitoringChart />
            </div>
            <div className="md:col-span-1">
              <BackupStatusWidget />
            </div>
          </div>

          {/* Real System Info */}
          <div className="grid gap-4 md:grid-cols-2 mb-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">System Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Version:</span>
                  <span>{systemInfo.version}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Last Backup:</span>
                  <span>{systemInfo.lastBackup}</span>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Recent Activity</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {recentActivity.length > 0 ? (
                    recentActivity.map((activity, index) => (
                      <div key={index} className="text-sm flex justify-between">
                        <span className="flex items-center">
                          <span className="text-muted-foreground mr-2">•</span>
                          {activity.event}
                        </span>
                        <span className="text-muted-foreground text-xs">
                          {activity.timestamp}
                        </span>
                      </div>
                    ))
                  ) : (
                    <div className="text-sm text-muted-foreground">
                      Loading recent activity...
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Page Version Badge */}
          <div className="flex justify-end">
            <RevisionBadge pageKey="admin-dashboard-real-data-complete" />
          </div>
        </TabsContent>

        <TabsContent value="live-metrics">
          <LiveMetricsDashboard />
        </TabsContent>

        <TabsContent value="system-monitor">
          <RealtimeSystemMonitor />
        </TabsContent>

        <TabsContent value="backup">
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            <div className="md:col-span-2 lg:col-span-2">
              <SystemMonitoringChart />
            </div>
            <div className="md:col-span-1 lg:col-span-1">
              <BackupStatusWidget />
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </AdminLayout>
  );
}
